import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { api, getToken, setToken } from "./api";
import { T, T_NEW, colorFor } from "./theme.js";
import { I } from "./icons.jsx";
import { Av, Btn, Inp, Modal, Tabs } from "./components/ui.jsx";
import { NewGroupModal, NewTopicModal, NewChannelModal } from "./components/modals.jsx";
import { BalanceModal } from "./components/BalanceModal.jsx";
import { SettingsModal } from "./components/SettingsModal.jsx";
import { NewPostModal } from "./components/PostModal.jsx";
import { AdminPanel } from "./components/AdminPanel.jsx";
import { WarehouseQuickAddPage } from "./components/WarehouseQuickAddPage.jsx";
import { AuthScreen } from "./components/AuthScreen.jsx";
import { ChannelFeed } from "./components/ChannelFeed.jsx";
import { Dashboard } from "./components/Dashboard.jsx";
import { MyStore } from "./components/MyStore.jsx";
import { NotificationsModal } from "./components/NotificationsModal.jsx";
import { ensureSubscribedIfGranted } from "./pushSubscription.js";
import { FirstRunHint } from "./components/FirstRunHint.jsx";
import { SetupChecklist } from "./components/SetupChecklist.jsx";
import { ApplicationModal } from "./components/ApplicationModal.jsx";
import { OrdersModal } from "./components/OrdersModal.jsx";
import { EmojiPicker } from "./components/EmojiPicker.jsx";
import { CartModal } from "./components/CartModal.jsx";
import { useCart } from "./cart.js";
import { HelpModal } from "./components/HelpModal.jsx";
import { useToast } from "./components/toast.jsx";
import { LangProvider, useT, useLang, plural } from "./i18n.jsx";
import { chName, chNote } from "./channelLocale.js";

/* ═══ MAIN APP ═══ */
//
// Outer wrapper owns the active UI language (ru/en) so any component in
// the tree can switch it via the setLang callback — Settings does this
// when the customer picks a different language. The actual data /
// routing / state lives in AppInner so the LangProvider can re-render
// independently when language flips.
export default function App() {
  const [lang, setLang] = useState(getInitialLang());
  // Persist any language change so the next visit defaults to it.
  useEffect(() => { try { localStorage.setItem("cg_lang", lang); } catch {} }, [lang]);
  return (
    <LangProvider lang={lang}>
      <AppInner lang={lang} setLang={setLang} />
    </LangProvider>
  );
}

// Resolve initial language from URL ?lang= / localStorage; default to "ru"
// since customers are Russian-speaking. URL takes precedence so the same
// account can be tested in either language without flipping Settings.
function getInitialLang() {
  try {
    const url = new URLSearchParams(window.location.search).get("lang");
    if (url === "ru" || url === "en") return url;
  } catch {}
  try {
    const ls = localStorage.getItem("cg_lang");
    if (ls === "ru" || ls === "en") return ls;
  } catch {}
  return "ru";
}

function AppInner({ lang, setLang }) {
  const t = useT();
  const toast = useToast();
  const cartItems = useCart();
  const cartCount = cartItems.reduce((n, x) => n + (x.qty || 1), 0);
  const [user,setUser] = useState(null);
  const [booting,setBooting] = useState(true);
  const [users,setUsers] = useState([]);
  const [groups,setGroups] = useState([]);
  const [dmList,setDmList] = useState([]);
  const [channels,setChannels] = useState([]);          // Phase 2: list of all channels
  const [channelPosts,setChannelPosts] = useState([]);  // Phase 2: posts for the active channel
  const [archivedPosts, setArchivedPosts] = useState([]);  // archived posts → staff-only "Archived" view
  const [showArchived, setShowArchived] = useState(false); // feed shows archived-only when true
  const [active,setActive] = useState(null);
  const [msgs,setMsgs] = useState([]);
  const [input,setInput] = useState("");
  const [attachment,setAttachment] = useState(null); // {path, url, name} after upload
  const [attachUploading,setAttachUploading] = useState(false);
  const fileInputRef = useRef(null);
  const longPressRef = useRef(null);
  // Phase Z-2: per-message admin menu + inline edit
  const [msgMenu,setMsgMenu] = useState(null);      // open menu for message id (string)
  const [msgEdit,setMsgEdit] = useState(null);      // {id, text} when editing inline
  // Telegram-style message context menu (react/reply/copy/pin) + reply compose.
  const [msgCtx,setMsgCtx] = useState(null);        // { msg, x, y } | null
  const [replyTo,setReplyTo] = useState(null);      // message being replied to | null
  const [emojiPicker,setEmojiPicker] = useState(null); // { mode:"react"|"compose", msg?, position } | null
  const [showScrollDown,setShowScrollDown] = useState(false); // floating ↓ button
  const [pinnedIdx,setPinnedIdx] = useState(0);     // which pinned message the banner shows
  const [recording,setRecording] = useState(null);  // { sec } while recording a voice message
  const recRef = useRef(null);                       // { mr, chunks, timer }
  // In-chat search (DM/topic). The panel slides down below the chat header
  // when chatSearchOpen is true. Results are server-side substring matches
  // capped at 50; clicking one scrolls the chat to that message if loaded.
  const [chatSearchOpen,setChatSearchOpen] = useState(false);
  const [chatSearchQuery,setChatSearchQuery] = useState("");
  const [chatSearchResults,setChatSearchResults] = useState([]);
  const [chatSearchLoading,setChatSearchLoading] = useState(false);
  // DM templates (Feature 6). Loaded lazily when the operator opens
  // the picker the first time, then cached for the session. Picker
  // popover state lives here so closing happens via click-outside.
  const [dmTemplates, setDmTemplates] = useState(null);
  const [tmplPickerOpen, setTmplPickerOpen] = useState(false);
  const [search,setSearch] = useState("");
  const [tab,setTab] = useState("chats");
  const [modal,setModal] = useState(null);
  // Initial tab for SettingsModal — pinned by callers that want to
  // open Settings on a specific tab (currently only the Premium pill
  // since "forward"/"customers" moved to MyStore).
  const [settingsInitialTab,setSettingsInitialTab] = useState(null);
  // Initial tab for the MyStore surface (Главная / Клиенты /
  // Пересылка / Аналитика). Setting this and clearing `active` is how
  // the onboarding banner + the "Перейти к настройкам" jump-CTAs land
  // a user on a specific tab.
  const [myStoreInitialTab,setMyStoreInitialTab] = useState(null);
  // Bumped on Settings / MyStore close so SetupChecklist re-fetches
  // reseller settings (FX rate / forward targets) and disappears once
  // the reseller is configured. Cheap counter pattern beats wiring a
  // dedicated subscription.
  const [setupBump,setSetupBump] = useState(0);
  // Application-gate modal (migration 0052). Opens from the banner
  // shown above the channel feed for non-approved resellers.
  const [appGateOpen,setAppGateOpen] = useState(false);
  // "Forward" + "customers" + "stats" tabs moved to MyStore (the 🏠
  // surface) — the openX helpers therefore clear `active` and pin a
  // MyStore initial tab rather than opening SettingsModal.
  const openMyStore = (initialTab = "home") => {
    setMyStoreInitialTab(initialTab);
    setActive(null);
    if (isMobile) setMobSb(false);
  };
  const openForwardSettings = () => openMyStore("forward");
  const openCustomersSettings = () => openMyStore("customers");
  // Free-tier resellers click any "Premium-only" CTA (the dashed
  // 'Источник в Premium' pill on each card, etc.) → land directly on
  // the upgrade pitch in Settings. Premium stayed in Settings because
  // it's an account-level purchase rather than a store-management
  // surface.
  const openPremiumSettings = () => {
    setSettingsInitialTab("premium");
    setModal("settings");
  };
  const [mobSb,setMobSb] = useState(true);
  // Bell-icon unread count. Polls every 45s + bumps live from the
  // existing SSE events stream (events handlers below). Display
  // clamps at "99+" when the user has neglected the feed.
  const [unreadCount, setUnreadCount] = useState(0);
  const refreshUnread = useCallback(async () => {
    try {
      const r = await api.getUnreadNotificationCount();
      setUnreadCount(r?.count ?? 0);
    } catch {}
  }, []);
  useEffect(() => {
    if (!user) return;
    refreshUnread();
    const iv = setInterval(refreshUnread, 45000);
    return () => clearInterval(iv);
  }, [user, refreshUnread]);
  // Auto-resubscribe to Web Push when the user has already granted
  // notification permission on a previous visit and the server has a
  // VAPID public key. No prompt — the explicit opt-in lives inside
  // NotificationsModal for first-time users.
  useEffect(() => {
    if (!user?.webPushPublicKey) return;
    ensureSubscribedIfGranted(user.webPushPublicKey);
  }, [user]);
  const endRef = useRef(null);
  const scrollRef = useRef(null);        // messages scroll container
  const forceBottomRef = useRef(true);   // true = jump to bottom on next msgs render (thread open / sent)
  const returnTargetRef = useRef(null);  // msgId to return to after jumping up via a reply (Telegram-style)
  const pollRef = useRef(null);
  // Track the most recent in-flight request key so a slow response for a
  // channel/chat the user already navigated away from can't overwrite the
  // current view (stale-response race on throttled mobile networks).
  const latestChannelReqRef = useRef(null);
  const latestMsgsKeyRef = useRef(null);

  // Boot
  useEffect(() => {
    (async () => {
      if (getToken()) {
        try { const r = await api.me(); setUser(r.user); } catch { setToken(null); }
      }
      setBooting(false);
    })();
  }, []);

  // Load data.
  //
  // Phase 1 role-aware split: customers can't list other users, so for them
  // we fetch /api/managers (which only returns staff and is safe). Managers
  // and admins still call /api/users for the full directory.
  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      const userListPromise =
        user.role === "customer" ? api.getManagers() : api.getUsers();
      const [u, g, d, ch] = await Promise.all([
        userListPromise,
        api.getGroups(),
        api.getDms(),
        api.getChannels(),
      ]);
      setUsers(u);
      setGroups(g);
      setDmList(d);
      setChannels(ch);
    } catch (e) {
      console.error(e);
    }
  }, [user]);

  // Load posts when active channel changes.
  //
  // Phase-0 channels can hold several hundred items per scrape cycle
  // (curated-shoes was 382 after one Nordstrom Rack pass on 2026-06-02)
  // and ChannelFeed.jsx applies brand/price/discount filters + sort
  // client-side via useMemo. Fetching 500 in one shot avoids stale-list
  // bugs that come with mixing server-side pagination and client-side
  // filtering. Past ~2k posts per channel we'll need infinite scroll
  // (see TODO in apps/api/src/routes/posts.ts).
  const loadChannelPosts = useCallback(async (slug) => {
    if (!slug) { setChannelPosts([]); setArchivedPosts([]); return; }
    latestChannelReqRef.current = slug;
    try {
      // The API caps `limit` at 500 server-side, so page through with
      // `offset` until the whole channel is loaded (Walmart's OTC channel
      // alone has ~2.2k posts). The first page renders immediately; the
      // rest stream in and append, so client-side search + the post count
      // cover every post, while the feed itself renders incrementally
      // (ChannelFeed only mounts a page of cards at a time).
      const LIMIT = 500;
      let offset = 0;
      let all = [];
      for (;;) {
        const page = await api.getPosts({ channel: slug, limit: LIMIT, offset });
        // Drop the response if the user has since switched channels —
        // otherwise a slower earlier fetch can clobber the newer feed.
        if (latestChannelReqRef.current !== slug) return;
        all = offset === 0 ? page : all.concat(page);
        // Split by status: the feed shows live/working posts; archived ones go
        // to the staff-only "Archived" view (restorable). Rejected are dropped.
        setChannelPosts(all.filter(p => p.status !== "archived" && p.status !== "rejected"));
        setArchivedPosts(all.filter(p => p.status === "archived"));
        if (page.length < LIMIT) break;   // last (partial) page → done
        offset += LIMIT;
      }
    } catch (e) {
      if (latestChannelReqRef.current !== slug) return;
      console.error(e);
      setChannelPosts([]);
      setArchivedPosts([]);
    }
  }, []);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  // Phase 2: when active channel changes, load its posts
  useEffect(() => {
    setShowArchived(false);   // start each channel in the normal (non-archived) view
    if (active?.type === "channel") loadChannelPosts(active.slug);
    else { setChannelPosts([]); setArchivedPosts([]); }
  }, [active, loadChannelPosts]);

  // Poll sidebar list every 15 seconds as a safety net behind SSE push.
  // SSE handles the fast path (instant updates on incoming DMs / order intents).
  // The poll just covers the case where the SSE connection silently dropped.
  const sidebarPollRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    sidebarPollRef.current = setInterval(loadData, 15000);
    return () => { if (sidebarPollRef.current) clearInterval(sidebarPollRef.current); };
  }, [user, loadData]);

  // ─── Server-Sent Events: live push for new DMs / order intents ─────────
  //
  // EventSource cannot send Authorization headers, so we pass the JWT as a
  // query string. Server validates it the same way requireAuth does.
  // On any event payload, we refresh the sidebar (unread counts, DM list);
  // if the active chat is the one with new activity, we reload its messages
  // too. Browser auto-reconnects with backoff if the stream drops.
  //
  // We use refs to access the latest active/loadData/loadMsgs from inside the
  // long-lived handler without re-creating the EventSource on every change.
  const activeRef = useRef(active);
  useEffect(() => { activeRef.current = active; }, [active]);
  const loadDataRef = useRef(loadData);
  useEffect(() => { loadDataRef.current = loadData; }, [loadData]);
  const loadChannelPostsRef = useRef(loadChannelPosts);
  useEffect(() => { loadChannelPostsRef.current = loadChannelPosts; }, [loadChannelPosts]);
  // NOTE: initialize with null, not loadMsgs — `loadMsgs` is declared below
  // and reading it here would hit the temporal dead zone. The useEffect after
  // its declaration assigns the current value into this ref.
  const loadMsgsRef = useRef(null);

  // Helper: should we fire a native OS notification for this incoming event?
  // Yes when: permission granted, the tab isn't visible OR the user isn't
  // viewing THIS conversation, and the sender isn't this user (no self-pings).
  const maybeNotify = useCallback((data, currentUid) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (data.senderId === currentUid) return;
    const cur = activeRef.current;
    const lookingAtThisDm = cur?.type === "dm" && cur.id === data.dmId && document.visibilityState === "visible";
    if (lookingAtThisDm) return;
    const title = data.senderName || "New message";
    const body = data.textSnippet?.trim()
      ? data.textSnippet
      : data.hasAttachment ? "📎 Sent an attachment" : "New message";
    try {
      const n = new Notification(title, {
        body,
        tag: `cg-dm-${data.dmId}`,   // collapse multiple sends from same DM
        icon: "/icon.svg",
        badge: "/icon.svg",
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch { /* some browsers throw on rapid notify; ignore */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    const tok = getToken();
    if (!tok) return;
    const es = new EventSource(`/api/events?token=${encodeURIComponent(tok)}`);
    es.onmessage = (e) => {
      let data = null;
      try { data = JSON.parse(e.data); } catch { return; }
      if (!data || !data.type) return;
      if (data.type === "dm_message") {
        loadDataRef.current?.();              // unread badge + sidebar order
        const cur = activeRef.current;
        if (cur?.type === "dm" && cur.id === data.dmId) {
          loadMsgsRef.current?.();
        }
        maybeNotify(data, user.uid);
      } else if (data.type === "message_reaction" || data.type === "message_pin") {
        // Refresh the open thread so reactions / pins from the other side
        // appear live.
        if (activeRef.current?.type === "dm") loadMsgsRef.current?.();
      } else if (data.type === "dm_created") {
        // A new DM thread was just created with us as a participant — refresh
        // the sidebar so it appears without waiting for the 15s poll.
        loadDataRef.current?.();
      } else if (data.type === "post_published") {
        loadDataRef.current?.();
        const cur = activeRef.current;
        if (cur?.type === "channel" && cur.slug === data.channelSlug) {
          loadChannelPostsRef.current?.(data.channelSlug);
        }
      }
    };
    es.onerror = () => { /* browser auto-reconnects */ };
    return () => es.close();
  }, [user, maybeNotify]);

  // ─── Push-notification deep links ───
  // When a web-push notification is clicked, the service worker either
  // focuses this tab and postMessages {type:"notif-click", url}, or opens
  // a fresh window at that url (e.g. /?dm=<id> or /?notif=orders). Handle
  // both so the click lands on the referenced thread / screen.
  useEffect(() => {
    if (!user) return;
    const go = (rawUrl) => {
      let q;
      try { q = new URL(rawUrl, window.location.origin).searchParams; } catch { return; }
      const dm = q.get("dm");
      const notif = q.get("notif");
      if (dm) {
        setTab("chats");
        setActive({ type: "dm", id: dm });
        if (window.innerWidth < 768) setMobSb(false);
        loadData();
      } else if (notif === "orders") { setModal("orders"); }
      else if (notif === "balance") { setModal("balance"); }
      else if (notif === "chats") { setTab("chats"); }
    };
    // 1. Fresh window opened by the SW carries the link in the URL.
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("dm") || sp.get("notif")) {
      go(window.location.href);
      // Strip the params so a refresh doesn't re-trigger the jump.
      window.history.replaceState({}, "", window.location.pathname);
    }
    // 2. Already-open tab gets a postMessage from the SW on click.
    const onMsg = (e) => {
      if (e.data && e.data.type === "notif-click" && e.data.url) go(e.data.url);
    };
    navigator.serviceWorker?.addEventListener?.("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener?.("message", onMsg);
  }, [user]);

  // Ask once for Notification permission shortly after login. Browsers
  // ignore permission prompts that aren't tied to a user gesture, so we
  // don't push hard — if "default" stays default, no harm done.
  useEffect(() => {
    if (!user) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "default") return;
    // Small delay so it doesn't flash up alongside the login transition
    const t = setTimeout(() => {
      Notification.requestPermission().catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [user]);

  // Load messages for active chat + poll. Only applies to topic/dm chats;
  // channels (Phase 2) use a different data path and must not be polled here.
  const loadMsgs = useCallback(async () => {
    if (!active || (active.type !== "topic" && active.type !== "dm")) return;
    const key = active.type==="topic"?`topic:${active.id}`:`dm:${active.id}`;
    latestMsgsKeyRef.current = key;
    try {
      const m = await api.getMessages(key);
      // Ignore a poll/response that resolved after the user opened a
      // different chat — it would otherwise repaint the new thread with
      // the previous conversation's messages until the next poll.
      if (latestMsgsKeyRef.current !== key) return;
      setMsgs(m);
      // For DMs, refresh the read cursor on each poll so unread count stays at 0
      // while the user is actively viewing the conversation.
      if (active.type === "dm") api.markDmRead(active.id).catch(()=>{});
    } catch {}
  }, [active]);

  // Keep the ref in sync so the long-lived SSE handler always calls the
  // latest loadMsgs (which is recreated whenever `active` changes).
  useEffect(() => { loadMsgsRef.current = loadMsgs; }, [loadMsgs]);

  // Close in-chat search whenever the active conversation changes
  useEffect(() => {
    setChatSearchOpen(false);
    setChatSearchQuery("");
    setChatSearchResults([]);
  }, [active]);

  // Debounced search: 250ms after the user stops typing, hit the server.
  // Empty query → clear results without making a request.
  useEffect(() => {
    if (!chatSearchOpen) return;
    if (!active || (active.type !== "dm" && active.type !== "topic")) return;
    const q = chatSearchQuery.trim();
    if (!q) { setChatSearchResults([]); setChatSearchLoading(false); return; }
    setChatSearchLoading(true);
    const channelKey = active.type === "topic" ? `topic:${active.id}` : `dm:${active.id}`;
    const t = setTimeout(async () => {
      try {
        const r = await api.searchMessages(channelKey, q);
        setChatSearchResults(r);
      } catch (e) {
        console.error(e);
        setChatSearchResults([]);
      } finally {
        setChatSearchLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [chatSearchQuery, chatSearchOpen, active]);

  // Jump to a message: close search, scroll the matching element into view,
  // and briefly highlight it. If the message isn't in the loaded window
  // (older than the most recent 500), we just close the panel.
  const jumpToMessage = useCallback((msgId) => {
    setChatSearchOpen(false);
    // Wait one tick so the panel collapses and msgs are rendered
    setTimeout(() => {
      const el = document.querySelector(`[data-msgid="${msgId}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.transition = "background 0.4s";
      const orig = el.style.background;
      el.style.background = `${T.accent}33`;
      setTimeout(() => { el.style.background = orig; }, 1500);
    }, 50);
  }, []);

  useEffect(() => {
    forceBottomRef.current = true;   // opening a thread → land at the latest message
    loadMsgs();
    if (pollRef.current) clearInterval(pollRef.current);
    // Backstop poll behind SSE. SSE pushes new DM messages instantly, so a
    // 10s poll is enough to recover if the stream dropped without notifying.
    // Topic chats still rely on this poll exclusively for now.
    if (active && (active.type === "topic" || active.type === "dm")) {
      pollRef.current = setInterval(loadMsgs, 10000);
    }
    // Mark DM as read when opened. Sidebar poll will pick up the cleared count.
    if (active?.type === "dm") {
      api.markDmRead(active.id).catch(()=>{});
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [active, loadMsgs]);

  // Auto-scroll policy: jump to bottom only when opening a thread or just
  // after sending (forceBottomRef), or when the user is already near the
  // bottom. If they've scrolled up (e.g. tapped a pinned message), a
  // background poll/refresh must NOT yank them down.
  useEffect(() => {
    if (forceBottomRef.current) {
      forceBottomRef.current = false;
      endRef.current?.scrollIntoView();
      return;
    }
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    if (nearBottom) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const handleLogin = (u) => setUser(u);
  const handleLogout = () => {
    // Clear React + storage state first…
    setToken(null);
    setUser(null);
    setActive(null);
    // …then force a fresh page load. On iOS Safari the in-place
    // re-render path can fire effects with stale closures (one report
    // surfaced "Что-то сломалось" on logout) and the SW happily keeps
    // serving the old bundle. A replace() bypasses both: the next
    // navigation hits a clean AuthScreen with a fresh JS bundle and
    // no lingering subscribers.
    try { window.location.replace("/"); } catch { /* ignore */ }
  };

  const sendMsg = async () => {
    if (!active) return;
    if (active.type !== "topic" && active.type !== "dm") return;
    const text = input.trim();
    if (!text && !attachment) return;  // must have content
    const key = active.type==="topic"?`topic:${active.id}`:`dm:${active.id}`;
    const rt = replyTo;
    try {
      const msg = await api.sendMessage(key, text, attachment?.path || null, rt?.id || null);
      // Server's send response doesn't echo the reply object; attach locally
      // so the quote renders immediately.
      const local = rt
        ? { ...msg, replyTo: { id: rt.id, senderName: rt.senderName, text: rt.text, hasAttachment: !!rt.attachmentUrl } }
        : msg;
      forceBottomRef.current = true;   // always follow our own sent message
      setMsgs(prev => [...prev, local]);
      setInput("");
      setAttachment(null);
      setReplyTo(null);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior:"smooth" }), 50);
    } catch (e) { console.error(e); }
  };

  // ─── Message context-menu actions (react / reply / copy / pin) ───
  const REACTIONS = ["👍", "❤️", "👎", "🔥", "🥰", "👏", "😁"];
  const reactToMsg = async (msg, emoji) => {
    setMsgCtx(null);
    try {
      const r = await api.reactToMessage(msg.id, emoji);
      setMsgs(prev => prev.map(m => m.id === msg.id ? { ...m, reactions: r.reactions } : m));
    } catch (e) { toast.error(e.message); }
  };
  const togglePinMsg = async (msg) => {
    setMsgCtx(null);
    const willPin = !msg.pinnedAt;
    try {
      await api.pinMessage(msg.id, willPin);
      const now = Math.floor(Date.now() / 1000);
      // Single pinned per chat: pinning clears any other pin locally.
      setMsgs(prev => prev.map(m => {
        if (m.id === msg.id) return { ...m, pinnedAt: willPin ? now : null };
        return willPin ? { ...m, pinnedAt: null } : m;
      }));
    } catch (e) { toast.error(e.message); }
  };
  const copyMsg = (msg) => {
    setMsgCtx(null);
    navigator.clipboard?.writeText(msg.text || "").then(
      () => toast.success(t("app.message.copied")),
      () => {},
    );
  };
  const openReply = (msg) => { setMsgCtx(null); setReplyTo(msg); };
  const onEmojiPick = (emoji) => {
    if (emojiPicker?.mode === "react" && emojiPicker.msg) {
      reactToMsg(emojiPicker.msg, emoji);
      setEmojiPicker(null);
    } else {
      // Compose: insert and keep the picker open so the user can add more.
      setInput(prev => prev + emoji);
    }
  };

  // ─── Voice messages (MediaRecorder) ───
  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a mimeType the browser actually supports (Safari ≠ Chrome).
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mimeType = candidates.find(m => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(recRef.current?.timer);
        const cancelled = recRef.current?.cancelled;
        setRecording(null);
        if (cancelled) return;
        const type = mr.mimeType || "audio/webm";
        const blob = new Blob(chunks, { type });
        if (blob.size < 600) return; // ignore accidental taps
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        try {
          const up = await api.uploadAudio(blob, `voice.${ext}`);
          const key = active.type==="topic"?`topic:${active.id}`:`dm:${active.id}`;
          const msg = await api.sendMessage(key, "", up.path, null);
          forceBottomRef.current = true;
          setMsgs(prev => [...prev, msg]);
        } catch (e) { toast.error(e.message); }
      };
      recRef.current = { mr, chunks, cancelled: false, timer: setInterval(() => {
        setRecording(r => r ? { sec: r.sec + 1 } : r);
      }, 1000) };
      mr.start();
      setRecording({ sec: 0 });
    } catch {
      toast.error(t("app.voice.micDenied"));
    }
  };
  const stopRecording = (cancel) => {
    const r = recRef.current;
    if (!r) return;
    r.cancelled = !!cancel;
    try { r.mr.stop(); } catch {}
  };

  if (booting) return <div style={{ width:"100%",height:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",color:T.text,fontSize:14,fontFamily:'"IBM Plex Sans",-apple-system,sans-serif',letterSpacing:"0.02em" }}>{t("app.loading")}</div>;
  if (!user) return <AuthScreen onLogin={handleLogin} />;

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // Chat header info
  let chatTitle="", chatSub="", chatAvatarUrl=null;
  if (active?.type==="dm") {
    const dm = dmList.find(d=>d.id===active.id);
    // Public chat title — always the other user's @nickname, never
    // their ФИО. The handle is unique and the only public identifier.
    chatTitle = dm?.otherUser?.nickname
      ? `@${dm.otherUser.nickname}` : t("app.chatTitle.unknown");
    chatSub = t("app.chatSub.dm");
    chatAvatarUrl = dm?.otherUser?.avatarUrl || null;
  } else if (active?.type==="topic") {
    const grp = groups.find(g=>g.id===active.groupId);
    const topic = grp?.topics.find(t=>t.id===active.id);
    chatTitle = grp?.name || ""; chatSub = topic?`# ${topic.name}`:"";
  }

  // Filter on the public handle — the field that's actually rendered
  // in the row. ФИО isn't sent to the client for other users anyway.
  const filteredDms = dmList.filter(d =>
    (d.otherUser?.nickname || "").toLowerCase().includes((search || "").toLowerCase()));
  const myGroups = groups.filter(g => g.isMember);
  const availableGroups = groups.filter(g => !g.isMember);

  return (
    <div style={{ display:"flex",width:"100%",height:"100dvh",background:T.bg,fontFamily:"'Segoe UI',-apple-system,sans-serif",color:T.text,overflow:"hidden" }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
        /* Skeleton shimmer for the <Skeleton/> primitive — horizontal sweep
           on the gradient background. Disabled by the prefers-reduced-motion
           block in index.html so it auto-respects user prefs. */
        @keyframes cg-shimmer-anim {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .cg-shimmer { animation: cg-shimmer-anim 1.4s linear infinite; }
        /* Small pulse used by the sign-up nickname-availability dot
           while the debounced check is in flight. */
        @keyframes cg-pulse { 0%,100%{opacity:0.4} 50%{opacity:1} }

        *{box-sizing:border-box;margin:0;padding:0}
        /* Scrollbar — match the cream paper. Was dark navy thumb before. */
        ::-webkit-scrollbar{width:6px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(20,17,13,0.18);border-radius:3px}
        input::placeholder{color:${T.textSec}}
        html{height:-webkit-fill-available}
        body{min-height:100dvh;min-height:-webkit-fill-available}
        /* Hide scrollbar on horizontal tab rows — they scroll via swipe/wheel */
        .cg-tabs-scroll::-webkit-scrollbar{display:none}
        /* Modal: full-bleed bottom-sheet on phones. Driven by CSS rather
           than JS so it's correct on first paint and updates on rotate. */
        .cg-modal-grab { display: none; }
        @media (max-width: 640px) {
          .cg-modal-bg { padding: 0 !important; align-items: flex-end !important; }
          .cg-modal-body {
            width: 100% !important;
            max-width: 100% !important;
            max-height: 92dvh !important;
            border-radius: 16px 16px 0 0 !important;
            padding: 16px !important;
            /* overscroll-behavior: contain stops the page underneath
               from scrolling when the modal body bounces against its
               own scroll boundary. Pairs with useSwipeDownDismiss. */
            overscroll-behavior-y: contain;
            /* Allow vertical pan inside the body but tell the browser
               we may want to intercept downward gestures at the top —
               the hook calls preventDefault on touchmove when claiming
               the drag. */
            touch-action: pan-y;
          }
          /* Grab handle — drag affordance on the bottom sheet. */
          .cg-modal-grab {
            display: block;
            width: 36px; height: 4px;
            margin: -6px auto 12px;
            border-radius: 999px;
            background: rgba(20,17,13,0.18);
          }
        }
      `}</style>

      {/* SIDEBAR */}
      {(mobSb||!isMobile) && (
        <div style={{ width:isMobile?"100%":360,minWidth:isMobile?"100%":360,background:T.sidebar,display:"flex",flexDirection:"column",borderRight:`1px solid ${T.border}`,position:isMobile?"absolute":"relative",zIndex:10,height:"100%" }}>
          {/* Header. Wraps to a second row on narrow widths so the action
              icons never collide with the user's name/email. The user
              name+email block is allowed to shrink (minWidth:0) so it
              ellipsis-truncates rather than pushing the avatar block. */}
          <div style={{ padding:"10px 12px",paddingTop:"calc(10px + env(safe-area-inset-top, 0px))",borderBottom:`1px solid ${T.border}` }}>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap",rowGap:6 }}>
              <Av name={user.displayName} src={user.avatarUrl} size={36} />
              <div style={{ flex:"1 1 140px",minWidth:0 }}>
                <div style={{ fontSize:14,fontWeight:600,display:"flex",alignItems:"center",gap:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                  <span style={{ overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{user.displayName}</span>
                  {user.isAdmin&&I.admin()}
                </div>
                <div style={{ fontSize:11,color:T.textSec,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{user.email}</div>
              </div>
              {/* Action icon row — flex-end pushes everything right. All
                  buttons share a uniform 36×36 box (≥44px hit area when
                  you include the gap) so they read as a cohesive row
                  and meet HIG tap-target guidance on mobile. */}
              <div style={{ display:"flex",alignItems:"center",gap:6,flexShrink:0,marginLeft:"auto" }}>
                {/* Home button — clears `active` so the chat-area zone
                    falls back to the landing view (Dashboard for
                    resellers, welcome lockup for staff). Visible for
                    all roles since it's a general nav affordance. */}
                <button
                  onClick={() => {
                    setMyStoreInitialTab("home");
                    setActive(null);
                    if (isMobile) setMobSb(false);
                  }}
                  title={t("app.sidebar.store.title")}
                  style={{
                    width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center",
                    background: active === null ? T.accent : "none",
                    border: `1px solid ${T.accent}`,
                    color: active === null ? "#fff" : T.accent,
                    cursor: "pointer",
                    borderRadius: 8, padding: 0,
                  }}>
                  {I.store()}
                </button>
                {user.role === "customer" && (
                  <button onClick={()=>setModal("cart")} title={t("cart.title")}
                    style={{ width:36, height:36, position:"relative",
                             display:"flex", alignItems:"center", justifyContent:"center",
                             background:"none",border:`1px solid ${T.accent}`,color:T.accent,
                             cursor:"pointer",borderRadius:8,padding:0 }}>
                    {I.cart()}
                    {cartCount > 0 && (
                      <span style={{
                        position:"absolute", top:-4, right:-4,
                        minWidth: 16, height: 16, padding:"0 4px",
                        borderRadius: 999,
                        background: T.accent, color: "#fff",
                        fontSize: 10, fontWeight: 700, lineHeight: "16px",
                        textAlign: "center", fontFamily: T_NEW.fontMono,
                        boxShadow: "0 0 0 2px " + T.sidebar,
                      }}>
                        {cartCount > 99 ? "99+" : cartCount}
                      </span>
                    )}
                  </button>
                )}
                {user.role === "customer" && (
                  <button onClick={()=>setModal("orders")} title={t("orders.mine.title")}
                    style={{ width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center",
                             background:"none",border:`1px solid ${T.accent}`,color:T.accent,
                             cursor:"pointer",borderRadius:8,padding:0 }}>
                    {I.orders()}
                  </button>
                )}
                {/* Bell — opens NotificationsModal. Unread count badge
                    rendered inside as a tiny absolute-positioned dot
                    with the number; clamps at "99+". */}
                <button onClick={() => setModal("notifications")}
                        title={t("app.notif.bell.title")}
                        style={{ width:36, height:36, position:"relative",
                                 display:"flex", alignItems:"center", justifyContent:"center",
                                 background:"none", border:`1px solid ${T.accent}`,
                                 color: T.accent, cursor:"pointer",
                                 borderRadius:8, padding:0 }}>
                  {I.bell()}
                  {unreadCount > 0 && (
                    <span style={{
                      position:"absolute", top:-4, right:-4,
                      minWidth: 16, height: 16, padding:"0 4px",
                      borderRadius: 999,
                      background: T.accent, color: "#fff",
                      fontSize: 10, fontWeight: 700, lineHeight: "16px",
                      textAlign: "center",
                      fontFamily: T_NEW.fontMono,
                      boxShadow: "0 0 0 2px " + T.sidebar,
                    }}>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
                {/* Cashback wallet — reseller-only surface. Staff (admin
                    + manager) don't have a balance and the cashback
                    queue is dormant infra anyway, so we hide the icon
                    entirely rather than show a button that opens an
                    empty modal. */}
                {user.role === "customer" && (
                  <button onClick={()=>setModal("balance")} title={t("app.balance.title")}
                    style={{ width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center",
                             background:"none",border:`1px solid ${T.accent}`,color:T.accent,
                             cursor:"pointer",borderRadius:8,padding:0 }}>
                    {I.wallet()}
                  </button>
                )}
                {/* Help — opens HelpModal. Visible to all authenticated
                    users (staff included, since the FAQ is the same
                    reference doc for everyone; the "Contact support"
                    CTA inside is gated to customers only). */}
                <button onClick={()=>setModal("help")} title={t("app.help.title")}
                  style={{ width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center",
                           background:"none",border:`1px solid ${T.accent}`,color:T.accent,
                           cursor:"pointer",borderRadius:8,padding:0 }}>
                  {I.help()}
                </button>
                <button onClick={()=>setModal("settings")} title={t("app.settings.title")}
                  style={{ width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center",
                           background:"none",border:`1px solid ${T.accent}`,color:T.accent,
                           cursor:"pointer",borderRadius:8,padding:0 }}>
                  {I.settings()}
                </button>
                {/* Warehouse quick-add — staff only. Camera-fast publish
                    from inside a Costco / Ross / etc. aisle. Lives in the
                    sidebar header so it's reachable on every page without
                    navigating through menus. */}
                {(user.isAdmin || user.role === "manager") && (
                  <button onClick={()=>setModal("quickAdd")} title={t("sidebar.quickAdd")}
                    style={{ width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center",
                             background:"none",border:`1px solid ${T.border}`,color:T.accent,
                             cursor:"pointer",borderRadius:8,padding:0 }}>
                    {I.plus(T.accent)}
                  </button>
                )}
                <button onClick={handleLogout} title={t("app.logout.title")}
                  style={{ width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center",
                           background:"none",border:`1px solid ${T.border}`,color:T.danger,
                           cursor:"pointer",borderRadius:8,padding:0 }}>
                  {I.logout()}
                </button>
              </div>
            </div>
            <div style={{ position:"relative" }}>
              <input placeholder={t("tab.search")} value={search} onChange={e=>setSearch(e.target.value)} style={{ width:"100%",background:T.bg,border:"none",outline:"none",color:T.text,fontSize:14,padding:"9px 12px 9px 38px",borderRadius:20 }} />
              <div style={{ position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",display:"flex" }}>{I.search(T.textSec)}</div>
            </div>
          </div>

          <div style={{ padding:"8px 12px 0" }}>
            <Tabs tabs={[
              { id: "channels", label: t("tab.channels") },
              { id: "chats",    label: t("tab.chats") },
              // Groups are staff-only at the API layer (manager+). Hiding
              // the tab from customers prevents the silent-403 trap where
              // they'd see groups, click Join, and get nothing visible.
              ...(user.role !== "customer" ? [{ id: "groups", label: t("tab.groups") }] : []),
            ]} active={tab} onSelect={setTab} />
          </div>

          <div style={{ flex:1,overflowY:"auto" }}>
            {tab==="channels" && (
              <>
                {channels.length===0 && (
                  <div style={{ padding:"32px 24px",textAlign:"center",color:T.textSec }}>
                    <div style={{ fontSize:15,color:T.text,fontWeight:600,marginBottom:6 }}>{t("empty.channels.title")}</div>
                    <div style={{ fontSize:13,lineHeight:1.5 }}>
                      {user.isAdmin
                        ? t("sidebar.empty.channels.admin")
                        : t("empty.channels.customer")}
                    </div>
                    {/* Channel creation lives in AdminPanel → Настройки → Каналы.
                        Keeping it only there avoids the "two places to do the
                        same thing" confusion the sidebar empty-state used to cause. */}
                  </div>
                )}
                <ChannelsList
                  channels={channels}
                  search={search}
                  active={active}
                  isStaff={user.isAdmin || user.role === "manager"}
                  onPick={(ch) => {
                    setActive({ type: "channel", slug: ch.slug, id: ch.id, name: ch.name });
                    if (isMobile) setMobSb(false);
                  }}
                  T={T}
                  t={t}
                  plural={plural}
                />
              </>
            )}
            {tab==="chats" && (
              <>
                {filteredDms.length===0 && (
                  <div style={{ padding:"32px 24px",textAlign:"center",color:T.textSec }}>
                    <div style={{ fontSize:15,color:T.text,fontWeight:600,marginBottom:6 }}>
                      {search ? t("tab.search.noMatchDms") : t("empty.dms.title")}
                    </div>
                    {!search && (
                      <>
                        <div style={{ fontSize:13,lineHeight:1.5 }}>
                          {user.role === "customer"
                            ? t("empty.dms.customer")
                            : t("app.empty.staff.dmsCustomer")}
                        </div>
                        <button onClick={()=>setModal("newDm")} style={{ background:T.accent,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:600,marginTop:14,padding:"8px 16px",borderRadius:8 }}>
                          {user.role === "customer" ? t("empty.dms.cta.customer") : t("app.empty.staff.dmsCta")}
                        </button>
                      </>
                    )}
                  </div>
                )}
                {filteredDms.map(dm => (
                  <div key={dm.id} onClick={()=>{setActive({type:"dm",id:dm.id});if(isMobile)setMobSb(false);}}
                    style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 14px",cursor:"pointer",background:active?.id===dm.id&&active?.type==="dm"?T.sidebarActive:"transparent",transition:"background 0.12s" }}
                    onMouseEnter={e=>{if(!(active?.id===dm.id&&active?.type==="dm"))e.currentTarget.style.background=T.sidebarHover}}
                    onMouseLeave={e=>{if(!(active?.id===dm.id&&active?.type==="dm"))e.currentTarget.style.background="transparent"}}>
                    <Av name={dm.otherUser.nickname} src={dm.otherUser.avatarUrl} size={46} />
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ fontWeight:dm.unreadCount>0?700:500,fontSize:15 }}>@{dm.otherUser.nickname}</div>
                      <div style={{ fontSize:13,color:T.textSec }}>{dm.otherUser.email}</div>
                    </div>
                    {dm.unreadCount > 0 && !(active?.id===dm.id&&active?.type==="dm") && (
                      <div style={{ minWidth:22,height:22,borderRadius:11,background:T.accent,color:"#fff",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 7px",flexShrink:0 }}>
                        {dm.unreadCount > 99 ? "99+" : dm.unreadCount}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
            {tab==="groups" && (
              <>
                {availableGroups.filter(g=>(g.name||"").toLowerCase().includes((search||"").toLowerCase())).length>0 && <div style={{ padding:"8px 14px 4px",fontSize:11,color:T.textSec,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8 }}>{t("app.groups.available")}</div>}
                {availableGroups.filter(g=>(g.name||"").toLowerCase().includes((search||"").toLowerCase())).map(g => (
                  <div key={g.id} style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 14px" }}>
                    <Av name={g.name} size={46} />
                    <div style={{ flex:1 }}><div style={{ fontWeight:500,fontSize:15 }}>{g.name}</div><div style={{ fontSize:12,color:T.textSec }}>{t("app.groups.members", { n: g.members.length })}</div></div>
                    <Btn onClick={async()=>{
                            try { await api.joinGroup(g.id); await loadData(); }
                            catch (e) { toast.error(e.message); }
                          }}
                         style={{ padding:"6px 14px",fontSize:12 }}>{t("app.groups.join")}</Btn>
                  </div>
                ))}
                {myGroups.length>0 && <div style={{ padding:"12px 14px 4px",fontSize:11,color:T.textSec,fontWeight:700,textTransform:"uppercase",letterSpacing:0.8 }}>{t("app.groups.my")}</div>}
                {myGroups.filter(g=>(g.name||"").toLowerCase().includes((search||"").toLowerCase())).map(g => (
                  <div key={g.id}>
                    <div style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 14px" }}>
                      <Av name={g.name} size={46} />
                      <div style={{ flex:1 }}><div style={{ fontWeight:600,fontSize:15 }}>{g.name}</div><div style={{ fontSize:12,color:T.textSec }}>{t("app.groups.topics", { n: g.members.length, t: g.topics?.length||0 })}</div></div>
                      {!user.isAdmin && <button onClick={async()=>{
                            try { await api.leaveGroup(g.id); await loadData(); }
                            catch (e) { toast.error(e.message); }
                          }}
                        style={{ background:"none",border:"none",cursor:"pointer",fontSize:11,color:T.danger }}>{t("app.groups.leave")}</button>}
                    </div>
                    <div style={{ paddingLeft:24,paddingBottom:4 }}>
                      {(g.topics||[]).map(topic => {
                        const isA = active?.type==="topic"&&active.id===topic.id;
                        return (
                          <div key={topic.id} onClick={()=>{setActive({type:"topic",id:topic.id,groupId:g.id});if(isMobile)setMobSb(false);}}
                            style={{ display:"flex",alignItems:"center",gap:8,padding:"7px 12px",cursor:"pointer",borderRadius:8,marginBottom:2,background:isA?T.sidebarActive:"transparent",transition:"background 0.12s" }}
                            onMouseEnter={e=>{if(!isA)e.currentTarget.style.background=T.sidebarHover}}
                            onMouseLeave={e=>{if(!isA)e.currentTarget.style.background="transparent"}}>
                            {I.hash()}<div style={{ flex:1,minWidth:0 }}><div style={{ fontSize:14,fontWeight:500 }}>{topic.name}</div></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {groups.length===0 && (
                  <div style={{ padding:"32px 24px",textAlign:"center",color:T.textSec }}>
                    <div style={{ fontSize:15,color:T.text,fontWeight:600,marginBottom:6 }}>{t("sidebar.empty.groups.title")}</div>
                    <div style={{ fontSize:13,lineHeight:1.5 }}>
                      {user.isAdmin
                        ? t("sidebar.empty.groups.admin")
                        : t("sidebar.empty.groups.customer")}
                    </div>
                    {/* Group creation lives in AdminPanel → Настройки → Groups & Topics. */}
                  </div>
                )}
              </>
            )}
          </div>
          {(() => {
            // FAB context: which modal does "+" open in the current tab?
            // Group + channel creation used to also hang off the FAB for
            // admins, but those actions now live in AdminPanel →
            // Настройки → Groups & Topics / Каналы. Two entry points
            // for the same thing was confusing — the FAB is now a
            // chats-only action so it stays unambiguous: "+" = "start
            // a conversation."
            let target = null;
            if (tab === "chats") target = "newDm";
            if (!target) return null;
            return (
              <button onClick={()=>setModal(target)}
                style={{ position:"absolute",bottom:20,right:20,width:54,height:54,borderRadius:"50%",background:T.accent,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 4px 16px ${T.accent}44`,zIndex:5 }}>
                {I.plus("#fff")}
              </button>
            );
          })()}
        </div>
      )}

      {/* CHAT AREA */}
      <div style={{ flex:1,display:"flex",flexDirection:"column",background:T.chatBg }}>
        {active?.type === "channel" ? (
          <>
            {/* Application gate banner — only renders for customer-role
                accounts whose application_status isn't 'approved'.
                Sits above the SetupChecklist so it reads first; click
                opens the proof-of-ownership form. */}
            {user.role === "customer" && user.applicationStatus && user.applicationStatus !== "approved" && (
              <div style={{
                margin: "12px 16px 0",
                padding: "12px 14px",
                background: user.applicationStatus === "submitted"
                  ? `${T_NEW.success}10`
                  : user.applicationStatus === "rejected"
                    ? `${T_NEW.danger}10`
                    : `${T_NEW.persimmon}14`,
                border: `1px solid ${user.applicationStatus === "submitted"
                  ? T_NEW.success + "44"
                  : user.applicationStatus === "rejected"
                    ? T_NEW.danger + "44"
                    : T_NEW.persimmon + "44"}`,
                borderRadius: T_NEW.r2,
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: T_NEW.ink, marginBottom: 2 }}>
                    {user.applicationStatus === "submitted"
                      ? t("app.applicationBanner.submitted.title")
                      : user.applicationStatus === "rejected"
                        ? t("app.applicationBanner.rejected.title")
                        : t("app.applicationBanner.pending.title")}
                  </div>
                  <div style={{ fontSize: 13, color: T_NEW.ink56,
                                fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
                    {user.applicationStatus === "submitted"
                      ? t("app.applicationBanner.submitted.body")
                      : user.applicationStatus === "rejected"
                        ? t("app.applicationBanner.rejected.body")
                        : t("app.applicationBanner.pending.body")}
                  </div>
                </div>
                <Btn variant="primary" onClick={() => setAppGateOpen(true)}>
                  {user.applicationStatus === "submitted"
                    ? t("app.applicationBanner.view")
                    : t("app.applicationBanner.openApp")}
                </Btn>
              </div>
            )}
            {/* Reseller onboarding banner — only renders for accounts
                missing FX rate or a forward target, and disappears
                once both are configured. Sits above the feed so it's
                impossible to miss but doesn't push the feed below the
                fold on desktop. */}
            <SetupChecklist
              user={user}
              bump={setupBump}
              onOpenForwardSettings={openForwardSettings}
            />
            <ChannelFeed
              user={user}
              active={active}
              channels={channels}
              channelPosts={showArchived ? archivedPosts : channelPosts}
              archivedCount={archivedPosts.length}
              showArchived={showArchived}
              onToggleArchived={() => setShowArchived(v => !v)}
              isMobile={isMobile}
              onBack={() => setMobSb(true)}
              onCreatePost={() => setModal({ type: "newPost", channelSlug: active.slug })}
              onAfterPostChange={() => loadChannelPosts(active.slug)}
              onOrderIntent={async (dmId) => {
                await loadData();
                setTab("chats");
                setActive({ type: "dm", id: dmId });
                if (isMobile) setMobSb(false);
              }}
              onOpenForwardSettings={openForwardSettings}
              onOpenPremiumSettings={openPremiumSettings}
              onJumpToChannel={(slug) => {
                // CodeLookupModal asked us to navigate to a different
                // channel. Match by slug, mirror what the sidebar
                // click handler does. Channels list comes from /api;
                // a missing channel (deleted between lookup + jump)
                // silently no-ops.
                const ch = (channels ?? []).find((c) => c.slug === slug);
                if (ch) {
                  setActive({ type: "channel", slug: ch.slug, id: ch.id, name: ch.name });
                  setTab("channels");
                  if (isMobile) setMobSb(false);
                }
              }}
            />
          </>
        ) : active ? (
          <>
            <div style={{ padding:"0 16px",paddingTop:"env(safe-area-inset-top, 0px)",display:"flex",alignItems:"center",gap:12,minHeight:56,background:T.sidebar,borderBottom:`1px solid ${T.border}` }}>
              {isMobile && <button onClick={()=>setMobSb(true)} style={{ background:"none",border:"none",cursor:"pointer",padding:4,display:"flex" }}>{I.back()}</button>}
              <Av name={chatTitle} src={chatAvatarUrl} size={40} />
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontFamily:T_NEW.fontSerif,fontWeight:500,fontSize:16,color:T_NEW.ink,
                              lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                  {chatTitle}
                </div>
                <div style={{ fontFamily:T_NEW.fontMono,fontSize:T_NEW.micro,color:T_NEW.persimmon,
                              textTransform:"uppercase",letterSpacing:T_NEW.monoTrack,marginTop:3 }}>
                  {chatSub}
                </div>
              </div>
              <button
                onClick={()=>{
                  setChatSearchOpen(o => !o);
                  if (chatSearchOpen) { setChatSearchQuery(""); setChatSearchResults([]); }
                }}
                title={t("app.chatSearch.title")}
                style={{ background:"none",border:"none",cursor:"pointer",padding:8,display:"flex",opacity:chatSearchOpen?1:0.7 }}
              >{I.search(chatSearchOpen ? T.accent : T.textSec)}</button>
              {active.type==="topic"&&user.isAdmin && <button onClick={()=>setModal({type:"addTopic",groupId:active.groupId})} style={{ background:"none",border:"none",cursor:"pointer",padding:8,display:"flex" }}>{I.plus()}</button>}
            </div>
            {chatSearchOpen && (
              <div style={{ background:T_NEW.paperSoft,borderBottom:`1px solid ${T_NEW.ink12}`,
                            padding:"12px 16px",display:"flex",flexDirection:"column",gap:10,
                            maxHeight:"50vh",overflow:"hidden" }}>
                <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                  <div style={{ flex:1,position:"relative" }}>
                    <div style={{ position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",display:"flex",opacity:0.65 }}>
                      {I.search()}
                    </div>
                    <input
                      autoFocus
                      placeholder={t("app.chatSearch.placeholder")}
                      value={chatSearchQuery}
                      onChange={(e)=>setChatSearchQuery(e.target.value)}
                      onKeyDown={(e)=>{ if (e.key==="Escape") { setChatSearchOpen(false); setChatSearchQuery(""); setChatSearchResults([]); } }}
                      style={{ width:"100%",background:T_NEW.paper,border:`1px solid ${T_NEW.ink12}`,outline:"none",
                               color:T_NEW.ink,fontSize:T_NEW.body,padding:"10px 12px 10px 38px",borderRadius:T_NEW.r1,
                               fontFamily:T_NEW.fontSans }}
                    />
                  </div>
                  <button onClick={()=>{ setChatSearchOpen(false); setChatSearchQuery(""); setChatSearchResults([]); }}
                          style={{ background:"none",border:"none",cursor:"pointer",padding:6,display:"flex",color:T_NEW.ink56 }}>
                    {I.close()}
                  </button>
                </div>
                <div style={{ flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:6 }}>
                  {chatSearchLoading && (
                    <div style={{ color:T_NEW.ink56,fontSize:T_NEW.meta,padding:"6px 4px",
                                  fontFamily:T_NEW.fontMono,textTransform:"uppercase",letterSpacing:T_NEW.monoTrack }}>
                      {t("app.chatSearch.searching")}
                    </div>
                  )}
                  {!chatSearchLoading && chatSearchQuery.trim() && chatSearchResults.length === 0 && (
                    <div style={{ color:T_NEW.ink56,fontSize:T_NEW.bodySm,padding:"12px 4px",
                                  fontFamily:T_NEW.fontSerif,fontStyle:"italic",textAlign:"center" }}>
                      {t("app.chatSearch.empty")}
                    </div>
                  )}
                  {chatSearchResults.map(r => {
                    const dt = new Date(r.ts * 1000);
                    const dateStr = dt.toLocaleDateString([], { month:"short", day:"numeric" }) + " " +
                                    dt.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit", hour12:false });
                    return (
                      <button key={r.id}
                              onClick={()=>jumpToMessage(r.id)}
                              style={{ textAlign:"left",background:T_NEW.paper,border:`1px solid ${T_NEW.ink12}`,
                                       borderRadius:T_NEW.r1,padding:"10px 12px",cursor:"pointer",
                                       display:"flex",gap:10,alignItems:"flex-start" }}>
                        <Av name={r.senderName||"?"} size={28} />
                        <div style={{ flex:1,minWidth:0 }}>
                          <div style={{ display:"flex",justifyContent:"space-between",gap:8,marginBottom:3 }}>
                            <span style={{ color:colorFor(r.senderName||"?"),fontSize:T_NEW.meta,fontWeight:700,
                                           fontFamily:T_NEW.fontSerif,fontStyle:"italic" }}>
                              {r.senderName}
                            </span>
                            <span style={{ color:T_NEW.ink56,fontSize:T_NEW.micro,fontFamily:T_NEW.fontMono }}>
                              {dateStr}
                            </span>
                          </div>
                          <div style={{ color:T_NEW.ink,fontSize:T_NEW.bodySm,whiteSpace:"nowrap",overflow:"hidden",
                                        textOverflow:"ellipsis",fontFamily:T_NEW.fontSans }}>
                            {r.text}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Pinned messages banner — cycles through multiple pins. */}
            {(() => {
              const pins = msgs.filter(m => m.pinnedAt);
              if (pins.length === 0) return null;
              const idx = pinnedIdx % pins.length;
              const pinned = pins[idx];
              return (
                <div style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 16px",
                              background:T_NEW.paperRaised,borderBottom:`1px solid ${T_NEW.ink12}`,
                              cursor:"pointer" }}
                     onClick={()=>{
                       document.querySelector(`[data-msgid="${pinned.id}"]`)?.scrollIntoView({behavior:"smooth",block:"center"});
                       if (pins.length > 1) setPinnedIdx((idx + 1) % pins.length);
                     }}>
                  <span style={{ fontSize:15 }}>📌</span>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontSize:T_NEW.micro,color:T_NEW.persimmon,fontWeight:700,
                                  fontFamily:T_NEW.fontMono,textTransform:"uppercase",letterSpacing:T_NEW.monoTrack }}>
                      {t("app.message.pinned")}{pins.length > 1 ? ` ${idx + 1}/${pins.length}` : ""}
                    </div>
                    <div style={{ fontSize:T_NEW.meta,color:T_NEW.ink,overflow:"hidden",
                                  textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                      {pinned.text || (pinned.attachmentUrl ? t("app.message.attachment") : "")}
                    </div>
                  </div>
                  <button onClick={(e)=>{ e.stopPropagation(); togglePinMsg(pinned); }}
                          title={t("app.message.menu.unpin")}
                          style={{ background:"none",border:"none",cursor:"pointer",color:T_NEW.ink56,
                                   fontSize:16,lineHeight:1,padding:4 }}>✕</button>
                </div>
              );
            })()}
            <div style={{ flex:1,position:"relative",display:"flex",flexDirection:"column",minHeight:0 }}>
            <div ref={scrollRef}
                 onScroll={(e)=>{ const el=e.currentTarget; setShowScrollDown(el.scrollHeight-el.scrollTop-el.clientHeight > 220); }}
                 style={{ flex:1,overflowY:"auto",padding:"12px 0" }}>
              {msgs.length===0 && (
                <div style={{ textAlign:"center",padding:"56px 28px",color:T_NEW.ink56,
                              maxWidth:420,margin:"0 auto" }}>
                  {/* BuyerGram stamp mark at quiet intensity. The page-as-
                      sealed-document metaphor reads as "a fresh page ready
                      for your first letter" rather than "nothing here." */}
                  <img src="/buyergram-icon-stamp.svg" alt=""
                       width={72} height={72} aria-hidden
                       style={{ marginBottom: 18, opacity: 0.65 }} />
                  <div style={{ fontFamily:T_NEW.fontSerif,fontSize:T_NEW.displayMd,color:T_NEW.ink,
                                fontWeight:500,marginBottom:10,lineHeight:1.25 }}>
                    {active.type === "dm" ? t("app.empty.dm.title") : t("app.empty.topic.title")}
                  </div>
                  <div style={{ fontSize:T_NEW.bodySm,lineHeight:1.55,fontFamily:T_NEW.fontSans,color:T_NEW.ink56 }}>
                    {active.type === "dm"
                      ? t("app.empty.dm.body")
                      : t("app.empty.topic.body")}
                  </div>
                </div>
              )}
              {msgs.map(msg => {
                const mine = msg.from===user.uid;
                return (
                  <div key={msg.id} data-msgid={msg.id} style={{ display:"flex",justifyContent:mine?"flex-end":"flex-start",padding:"2px 16px",animation:"fadeUp 0.15s" }}>
                    <div style={{ display:"flex",gap:8,maxWidth:"75%",flexDirection:mine?"row-reverse":"row",alignItems:"flex-end" }}>
                      {!mine && <Av name={msg.senderName||"?"} size={30} />}
                      <div
                        onContextMenu={(e)=>{ e.preventDefault(); setMsgCtx({ msg, x:e.clientX, y:e.clientY }); }}
                        onTouchStart={(e)=>{ const tp=e.touches[0]; const cx=tp.clientX, cy=tp.clientY; longPressRef.current=setTimeout(()=>setMsgCtx({ msg, x:cx, y:cy }), 450); }}
                        onTouchEnd={()=>clearTimeout(longPressRef.current)}
                        onTouchMove={()=>clearTimeout(longPressRef.current)}
                        style={{ position:"relative",padding:msg.attachmentUrl?"4px 4px 6px":"8px 14px 7px",
                                    borderRadius:T_NEW.r2,borderTopLeftRadius:mine?T_NEW.r2:T_NEW.r1,borderTopRightRadius:mine?T_NEW.r1:T_NEW.r2,
                                    background:mine?T.myMsg:T.theirMsg,color:mine?T_NEW.paperOnInk:T_NEW.ink,
                                    border:mine?"none":`1px solid ${T_NEW.ink12}`,overflow:"visible",maxWidth:"100%",
                                    fontFamily:T_NEW.fontSans,
                                    boxShadow:mine?"none":"0 1px 2px rgba(20,17,13,0.04)" }}>
                        {/* Reply quote — tap to jump to the original message */}
                        {msg.replyTo && (
                          <div onClick={(e)=>{ e.stopPropagation();
                                 returnTargetRef.current = msg.id;   // remember where we jumped from
                                 setShowScrollDown(true);
                                 jumpToMessage(msg.replyTo.id); }}
                               style={{ borderLeft:`3px solid ${mine?T_NEW.paperOnInk:T_NEW.persimmon}`,
                                        padding:"2px 8px",margin:msg.attachmentUrl?"4px 8px 6px":"0 0 6px",
                                        opacity:0.85,fontSize:T_NEW.meta,lineHeight:1.3,cursor:"pointer",
                                        background:mine?"rgba(240,233,220,0.12)":T_NEW.paperSoft,
                                        borderRadius:6 }}>
                            <div style={{ fontWeight:700,fontSize:T_NEW.micro }}>
                              {msg.replyTo.senderName || "—"}
                            </div>
                            <div style={{ overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:240 }}>
                              {msg.replyTo.text || (msg.replyTo.hasAttachment ? t("app.message.attachment") : "")}
                            </div>
                          </div>
                        )}
                        {!mine&&active.type==="topic" && (
                          <div style={{ fontSize:T_NEW.meta,fontWeight:600,
                                        fontFamily:T_NEW.fontSerif,fontStyle:"italic",
                                        color:colorFor(msg.senderName||"?"),marginBottom:3,
                                        padding:msg.attachmentUrl?"3px 8px 0":0 }}>
                            {msg.senderName}
                          </div>
                        )}
                        {msg.attachmentUrl && /\.(m4a|mp3|ogg|oga|webm|wav)$/i.test(msg.attachmentUrl) ? (
                          <audio controls src={msg.attachmentUrl}
                                 style={{ display:"block",width:240,maxWidth:"100%",height:40,margin:"2px 0 6px" }} />
                        ) : msg.attachmentUrl ? (
                          <a href={msg.attachmentUrl} target="_blank" rel="noopener noreferrer" style={{ display:"block",margin:"0 0 6px" }}>
                            <img src={msg.attachmentUrl} alt="" style={{ display:"block",maxWidth:"100%",maxHeight:320,borderRadius:8,objectFit:"cover" }} />
                          </a>
                        ) : null}
                        <div style={{ padding:msg.attachmentUrl?"0 8px":0 }}>
                          {msgEdit?.id === msg.id ? (
                            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
                              <textarea
                                value={msgEdit.text}
                                onChange={(e)=>setMsgEdit({...msgEdit,text:e.target.value})}
                                onKeyDown={(e)=>{ if (e.key==="Escape") setMsgEdit(null); }}
                                autoFocus
                                style={{ background:T.bg,color:T.text,border:`1px solid ${T.border}`,padding:"6px 8px",borderRadius:8,fontSize:14,minHeight:48,resize:"vertical",fontFamily:"inherit" }} />
                              <div style={{ display:"flex",justifyContent:"flex-end",gap:6 }}>
                                <button onClick={()=>setMsgEdit(null)} style={{ background:"none",border:"none",color:T.textSec,fontSize:12,cursor:"pointer",padding:"4px 8px" }}>{t("app.message.editingCancel")}</button>
                                <button onClick={async()=>{
                                  const txt = msgEdit.text.trim();
                                  if (!txt) return;
                                  try {
                                    const r = await api.editMessage(msg.id, txt);
                                    setMsgs(prev => prev.map(m => m.id===msg.id ? { ...m, text:r.text, editedAt:r.editedAt } : m));
                                    setMsgEdit(null);
                                  } catch (e) { toast.error(e.message); }
                                }} style={{ background:T.accent,color:"#fff",border:"none",padding:"4px 12px",borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer" }}>{t("app.message.editingSave")}</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {msg.text && (
                                <span style={{ fontSize:14.5,lineHeight:"22px",wordBreak:"break-word" }}>
                                  {msg.text}
                                </span>
                              )}
                              {msg.editedAt && (
                                <span style={{ fontSize:T_NEW.micro,
                                               color:mine?"rgba(240,233,220,0.55)":T_NEW.ink56,
                                               marginLeft:6,fontStyle:"italic",fontFamily:T_NEW.fontSerif }}>
                                  {t("app.message.edited")}
                                </span>
                              )}
                              <span style={{ float:"right",fontSize:T_NEW.micro,
                                             color:mine?"rgba(240,233,220,0.55)":T_NEW.ink56,
                                             marginLeft:10,position:"relative",top:5,
                                             fontFamily:T_NEW.fontMono,letterSpacing:T_NEW.monoTrack }}>
                                {msg.time}
                              </span>
                            </>
                          )}
                        </div>
                        {/* Reaction chips */}
                        {msg.reactions?.length > 0 && (
                          <div style={{ display:"flex",flexWrap:"wrap",gap:4,marginTop:5,
                                        padding:msg.attachmentUrl?"0 8px":0 }}>
                            {msg.reactions.map(rx => (
                              <button key={rx.emoji} onClick={()=>reactToMsg(msg, rx.emoji)}
                                style={{ display:"inline-flex",alignItems:"center",gap:3,
                                         padding:"1px 7px",borderRadius:999,cursor:"pointer",
                                         fontSize:12.5,lineHeight:"18px",fontFamily:T_NEW.fontSans,
                                         border:`1px solid ${rx.mine ? T_NEW.persimmon : (mine?"rgba(240,233,220,0.3)":T_NEW.ink12)}`,
                                         background:rx.mine ? (mine?"rgba(240,233,220,0.18)":T_NEW.persimmonSoft) : "transparent",
                                         color:mine?T_NEW.paperOnInk:T_NEW.ink }}>
                                <span>{rx.emoji}</span>
                                <span style={{ fontFamily:T_NEW.fontMono,fontSize:11 }}>{rx.count}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {/* Admin per-message menu (⋯) — editorial popover. */}
                        {user.isAdmin && msgEdit?.id !== msg.id && (
                          <>
                            <button
                              onClick={()=>setMsgMenu(msgMenu === msg.id ? null : msg.id)}
                              style={{ position:"absolute",top:2,[mine?"left":"right"]:-28,
                                       background:"none",border:"none",cursor:"pointer",padding:4,
                                       color:T_NEW.ink56,opacity:0.55,fontSize:18,lineHeight:1,
                                       fontFamily:T_NEW.fontMono }}>⋯</button>
                            {msgMenu === msg.id && (
                              <div style={{ position:"absolute",top:26,[mine?"left":"right"]:-28,
                                            background:T_NEW.paper,border:`1px solid ${T_NEW.ink12}`,
                                            borderRadius:T_NEW.r1,padding:4,zIndex:20,
                                            boxShadow:T_NEW.shadowCard,
                                            display:"flex",flexDirection:"column",minWidth:140 }}>
                                {msg.text && (
                                  <button onClick={()=>{ setMsgMenu(null); setMsgEdit({id: msg.id, text: msg.text}); }}
                                    style={{ background:"none",border:"none",color:T_NEW.ink,cursor:"pointer",
                                             padding:"8px 12px",fontSize:T_NEW.meta,textAlign:"left",
                                             borderRadius:T_NEW.r1,fontFamily:T_NEW.fontMono,
                                             textTransform:"uppercase",letterSpacing:T_NEW.monoTrack,fontWeight:600 }}>
                                    {t("app.message.menu.edit")}
                                  </button>
                                )}
                                <button onClick={async()=>{
                                  setMsgMenu(null);
                                  if (!await toast.confirm(t("app.message.deleteConfirm"))) return;
                                  try {
                                    await api.deleteMessage(msg.id);
                                    setMsgs(prev => prev.filter(m => m.id !== msg.id));
                                  } catch (e) { toast.error(e.message); }
                                }} style={{ background:"none",border:"none",color:T_NEW.danger,cursor:"pointer",
                                            padding:"8px 12px",fontSize:T_NEW.meta,textAlign:"left",
                                            borderRadius:T_NEW.r1,fontFamily:T_NEW.fontMono,
                                            textTransform:"uppercase",letterSpacing:T_NEW.monoTrack,fontWeight:600 }}>
                                  {t("app.message.menu.delete")}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            {/* Scroll-to-bottom button — appears when scrolled up */}
            {showScrollDown && (
              <button onClick={()=>{
                        if (returnTargetRef.current) {
                          const tgt = returnTargetRef.current; returnTargetRef.current = null;
                          jumpToMessage(tgt);            // first ↓ press → back to where the reply was
                        } else {
                          forceBottomRef.current=false;
                          endRef.current?.scrollIntoView({ behavior:"smooth" }); // then → bottom
                        }
                      }}
                      title={t("app.chat.scrollDown")}
                      style={{ position:"absolute",right:16,bottom:12,width:40,height:40,borderRadius:"50%",
                               background:T_NEW.paper,border:`1px solid ${T_NEW.ink12}`,cursor:"pointer",
                               boxShadow:T_NEW.shadowCard,display:"flex",alignItems:"center",justifyContent:"center",
                               color:T_NEW.ink,fontSize:20,lineHeight:1,zIndex:5 }}>↓</button>
            )}
            </div>
            <div style={{ padding:"10px 12px",paddingBottom:"calc(10px + env(safe-area-inset-bottom, 0px))",
                          background:T_NEW.paperSoft,borderTop:`1px solid ${T_NEW.ink12}` }}>
              {/* Attachment preview row — shown only when there's a pending image */}
              {(attachment || attachUploading) && (
                <div style={{ display:"flex",alignItems:"center",gap:10,padding:"6px 6px 10px" }}>
                  {attachUploading ? (
                    <div style={{ fontSize:T_NEW.micro,color:T_NEW.ink56,
                                  fontFamily:T_NEW.fontMono,textTransform:"uppercase",letterSpacing:T_NEW.monoTrack }}>
                      {t("app.attach.uploading")}
                    </div>
                  ) : (
                    <>
                      <div style={{ position:"relative",width:64,height:64,borderRadius:T_NEW.r1,overflow:"hidden",
                                    border:`1px solid ${T_NEW.ink12}` }}>
                        <img src={attachment.url} alt="" style={{ width:"100%",height:"100%",objectFit:"cover" }} />
                        <button onClick={()=>setAttachment(null)}
                                style={{ position:"absolute",top:3,right:3,width:20,height:20,borderRadius:"50%",
                                         background:"rgba(20,17,13,0.78)",border:"none",color:T_NEW.paperOnInk,
                                         cursor:"pointer",fontSize:13,lineHeight:1,
                                         display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>
                      </div>
                      <div style={{ fontSize:T_NEW.micro,color:T_NEW.ink56,
                                    fontFamily:T_NEW.fontSans,fontStyle:"italic" }}>
                        {attachment.name || t("app.attach.imageName")}
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* Reply compose bar */}
              {replyTo && (
                <div style={{ display:"flex",alignItems:"center",gap:10,padding:"6px 10px",marginBottom:6,
                              background:T_NEW.paperSoft,borderRadius:T_NEW.r1,
                              borderLeft:`3px solid ${T_NEW.persimmon}` }}>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontSize:T_NEW.micro,color:T_NEW.persimmon,fontWeight:700 }}>
                      {t("app.message.replyingTo", { name: replyTo.senderName || "—" })}
                    </div>
                    <div style={{ fontSize:T_NEW.meta,color:T_NEW.ink56,overflow:"hidden",
                                  textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                      {replyTo.text || (replyTo.attachmentUrl ? t("app.message.attachment") : "")}
                    </div>
                  </div>
                  <button onClick={()=>setReplyTo(null)}
                          style={{ background:"none",border:"none",cursor:"pointer",color:T_NEW.ink56,
                                   fontSize:16,lineHeight:1,padding:4 }}>✕</button>
                </div>
              )}
              {/* Input row — paperclip + template picker (staff only) + text + send */}
              <div style={{ display:"flex",alignItems:"flex-end",gap:6, position:"relative" }}>
                <button
                  onClick={()=>fileInputRef.current?.click()}
                  disabled={attachUploading}
                  title={t("app.attach.title")}
                  style={{ background:"none",border:"none",cursor:attachUploading?"default":"pointer",padding:8,display:"flex",opacity:attachUploading?0.4:0.8 }}>
                  {I.attach()}
                </button>
                {/* Template picker — staff-only. Opens a popover with the
                    operator's saved templates. Selecting one substitutes
                    {customer_name} with the DM recipient's name and
                    inserts the result into the message input. */}
                {user.role !== "customer" && active?.type === "dm" && (
                  <>
                    <button
                      onClick={async () => {
                        if (dmTemplates === null) {
                          try { setDmTemplates(await api.getDmTemplates()); }
                          catch (e) { toast.error(e.message); return; }
                        }
                        setTmplPickerOpen((x) => !x);
                      }}
                      title={t("app.tmpl.title")}
                      style={{ background:"none",border:`1px solid ${T_NEW.ink12}`,cursor:"pointer",
                               padding:"6px 10px",fontSize:T_NEW.micro,color:T_NEW.ink56,
                               fontFamily:T_NEW.fontMono,textTransform:"uppercase",letterSpacing:T_NEW.monoTrack,
                               fontWeight:700,borderRadius:T_NEW.r1 }}>
                      {t("app.tmpl.short")}
                    </button>
                    {tmplPickerOpen && (
                      <div style={{ position:"absolute", bottom:"100%", left:36, marginBottom:10,
                                    background:T_NEW.paper, border:`1px solid ${T_NEW.ink12}`,
                                    borderRadius:T_NEW.r2, boxShadow:T_NEW.shadowModal,
                                    minWidth:300, maxHeight:360, overflowY:"auto", zIndex:10 }}>
                        <div style={{ padding:"10px 14px", fontSize:T_NEW.micro, color:T_NEW.ink56,
                                      fontWeight:700, textTransform:"uppercase", letterSpacing:T_NEW.monoTrack,
                                      borderBottom:`1px solid ${T_NEW.ink12}`, fontFamily:T_NEW.fontMono,
                                      background:T_NEW.paperSoft }}>
                          {t("app.tmpl.heading")}
                        </div>
                        {(dmTemplates ?? []).length === 0 ? (
                          <div style={{ padding:"14px 16px", fontSize:T_NEW.meta, color:T_NEW.ink56,
                                        fontFamily:T_NEW.fontSerif, fontStyle:"italic", lineHeight:1.5 }}>
                            {t("app.tmpl.empty")}
                          </div>
                        ) : (
                          (dmTemplates ?? []).map((tpl) => (
                            <button key={tpl.id}
                                    onClick={() => {
                                      // Substitute placeholders. The DM list
                                      // gives us the other party's display
                                      // name; order_id only fills when the
                                      // DM has a context_post_id linked order
                                      // (we don't have that here yet — leaves
                                      // the placeholder literal so the
                                      // operator can hand-edit).
                                      const dm = dmList.find(d => d.id === active.id);
                                      const name = dm?.otherUser?.nickname
                                        ? `@${dm.otherUser.nickname}` : "";
                                      const raw = tpl.bodyRu || tpl.bodyEn || "";
                                      const subbed = raw.replace(/\{customer_name\}/g, name);
                                      setInput(subbed);
                                      setTmplPickerOpen(false);
                                    }}
                                    style={{ display:"block", width:"100%", textAlign:"left",
                                             padding:"12px 14px",
                                             background:"none", border:"none", cursor:"pointer",
                                             color:T_NEW.ink, borderBottom:`1px solid ${T_NEW.ink12}`,
                                             fontFamily:T_NEW.fontSans }}>
                              <div style={{ fontSize:T_NEW.bodySm, fontWeight:600, marginBottom:4,
                                            fontFamily:T_NEW.fontSerif }}>
                                {tpl.name}
                              </div>
                              <div style={{ fontSize:T_NEW.micro, color:T_NEW.ink56, lineHeight:1.5,
                                            overflow:"hidden", textOverflow:"ellipsis",
                                            display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>
                                {tpl.bodyRu || tpl.bodyEn}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display:"none" }}
                  onChange={async (e)=>{
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    setAttachUploading(true);
                    try {
                      const r = await api.uploadImage(f);
                      setAttachment({ path: r.path, url: r.url, name: f.name });
                    } catch (err) {
                      toast.error(t("app.upload.failed", { err: err.message || String(err) }));
                    }
                    setAttachUploading(false);
                  }} />
                <div style={{ flex:1,background:T_NEW.paper,borderRadius:T_NEW.r2,
                              border:`1px solid ${T_NEW.ink12}`,
                              display:"flex",alignItems:"center",padding:"0 4px" }}>
                  {!isMobile && !recording && (
                    <button onClick={(e)=>{ const r=e.currentTarget.getBoundingClientRect();
                              const W = typeof window!=="undefined"?window.innerWidth:400;
                              const H = typeof window!=="undefined"?window.innerHeight:800;
                              setEmojiPicker({ mode:"compose", position:{ bottom: H - r.top + 8,
                                left: Math.max(8, Math.min(r.left - 130, W - 308)) } }); }}
                            title={t("app.compose.emoji")}
                            style={{ background:"none",border:"none",cursor:"pointer",fontSize:19,
                                     padding:"0 4px",lineHeight:1 }}>😊</button>
                  )}
                  <input placeholder={recording ? t("app.voice.recording") : (attachment ? t("app.message.captionPlaceholder") : t("app.message.placeholder"))}
                    value={input} onChange={e=>setInput(e.target.value)} disabled={!!recording}
                    onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMsg();}}}
                    style={{ flex:1,background:"transparent",border:"none",outline:"none",color:T_NEW.ink,
                             fontSize:15,padding:"10px 14px",fontFamily:T_NEW.fontSans }} />
                </div>
                {recording ? (
                  <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                    <button onClick={()=>stopRecording(true)} title={t("common.cancel")}
                            style={{ background:"none",border:"none",cursor:"pointer",color:T_NEW.ink56,
                                     fontSize:18,padding:6 }}>✕</button>
                    <span style={{ fontFamily:T_NEW.fontMono,fontSize:T_NEW.meta,color:T_NEW.danger,minWidth:42 }}>
                      ● {Math.floor(recording.sec/60)}:{String(recording.sec%60).padStart(2,"0")}
                    </span>
                    <button onClick={()=>stopRecording(false)} title={t("app.voice.send")}
                            style={{ background:T_NEW.persimmon,border:"none",cursor:"pointer",
                                     padding:"10px 12px",display:"flex",borderRadius:T_NEW.r2,
                                     color:T_NEW.paperOnInk }}>
                      {I.send(T_NEW.paperOnInk)}
                    </button>
                  </div>
                ) : (input.trim()||attachment) ? (
                  <button onClick={sendMsg}
                          style={{ background:T_NEW.persimmon,
                                   border:"none",cursor:"pointer",
                                   padding:"10px 12px",display:"flex",
                                   borderRadius:T_NEW.r2,
                                   color:T_NEW.paperOnInk,
                                   transition:"background 160ms" }}>
                    {I.send(T_NEW.paperOnInk)}
                  </button>
                ) : (
                  <button onClick={startRecording} title={t("app.voice.record")}
                          style={{ background:"transparent",border:"none",cursor:"pointer",
                                   padding:8,display:"flex",fontSize:20,lineHeight:1,
                                   color:T_NEW.ink56 }}>🎤</button>
                )}
              </div>
            </div>
          </>
        ) : (
          /* "Мой магазин" — full-page surface with tabs Главная /
             Клиенты / Пересылка / Аналитика. Replaces the prior
             Dashboard render here. The home tab IS the dashboard,
             which is rendered inside MyStore's tab body. */
          <MyStore
            user={user}
            channels={channels}
            users={users}
            groups={groups}
            initialTab={myStoreInitialTab || "home"}
            isMobile={isMobile}
            onOpenSidebar={() => setMobSb(true)}
            onOpenOrders={() => setModal("orders")}
            onOpenPremium={openPremiumSettings}
            onOpenNotifications={() => setModal("notifications")}
            onRefresh={loadData}
            onCreateGroup={() => setModal("newGroup")}
            onAddTopic={(gid) => setModal({ type: "addTopic", groupId: gid })}
            onOpenDm={(dmId) => {
              // Order → "Chat with customer": open the DM thread directly.
              setModal(null);
              setTab("chats");
              setActive({ type: "dm", id: dmId });
              if (isMobile) setMobSb(false);
              loadData();
            }}
            // On mobile, switching the sidebar tab is invisible unless
            // we also open the drawer so the user can see the channel
            // list. Desktop sidebar is always visible so just setTab.
            onBrowseChannels={() => {
              setTab("channels");
              if (isMobile) setMobSb(true);
            }}
            toast={toast}
          />
        )}
      </div>

      {/* MODALS */}
      {modal==="newDm" && (
        <Modal title={t("app.newDm.title")} onClose={()=>setModal(null)}>
          {users.filter(u=>u.uid!==user.uid).length===0 ? (
            <p style={{ color:T.textSec,fontSize:14,textAlign:"center",padding:20,lineHeight:1.5 }}>
              {user.role === "customer"
                ? t("app.newDm.empty.customer")
                : t("app.newDm.empty.staff")}
            </p>
          ) : (
            <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
              {users.filter(u=>u.uid!==user.uid).map(u => (
                <div key={u.uid} onClick={async()=>{const r=await api.createDm(u.uid);setActive({type:"dm",id:r.id});setModal(null);setMobSb(false);loadData();}}
                  style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:10,cursor:"pointer",transition:"background 0.12s" }}
                  onMouseEnter={e=>e.currentTarget.style.background=T.sidebarHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <Av name={u.nickname || u.displayName} size={40} />
                  <div>
                    <div style={{ fontWeight:600,fontSize:14,display:"flex",alignItems:"center",gap:6 }}>
                      {u.nickname ? `@${u.nickname}` : u.displayName}
                      {u.isAdmin&&I.admin()}
                    </div>
                    <div style={{ fontSize:12,color:T.textSec }}>{u.email}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
      {modal==="newGroup" && <NewGroupModal onClose={()=>setModal(null)} onDone={loadData} />}
      {modal==="newChannel" && <NewChannelModal onClose={()=>setModal(null)} onDone={loadData} />}
      {modal==="balance" && <BalanceModal user={user} onClose={()=>setModal(null)} />}
      {modal==="help" && (
        <HelpModal
          user={user}
          onClose={() => setModal(null)}
          onOpenDm={(dmId) => {
            // Jump straight into the freshly-opened support DM:
            //   1) close the help modal,
            //   2) switch the sidebar to the chats tab so the thread
            //      is visible on the mobile back-button path too,
            //   3) set it as the active surface,
            //   4) refresh sidebar data so the new thread appears in
            //      the DM list even before the 15s poll catches up.
            setModal(null);
            setTab("chats");
            setActive({ type: "dm", id: dmId });
            if (isMobile) setMobSb(false);
            loadData();
          }}
        />
      )}
      {modal==="settings" && (
        <SettingsModal
          user={user}
          channels={channels}
          lang={lang}
          setLang={setLang}
          initialTab={settingsInitialTab}
          onClose={() => {
            // Bump the setup checklist so it re-fetches reseller
            // settings — if the user just saved an FX rate or a target,
            // the banner either updates or disappears. Reset the
            // initial-tab pin so the next normal "click ⚙" opens
            // Profile again.
            setModal(null);
            setSettingsInitialTab(null);
            setSetupBump((n) => n + 1);
          }}
          onUserUpdated={(u) => setUser((prev) => ({ ...prev, ...u }))}
          onChannelsChanged={loadData}
        />
      )}
      {modal==="notifications" && (
        <NotificationsModal
          vapidPublicKey={user?.webPushPublicKey || null}
          onClose={() => { setModal(null); refreshUnread(); }}
          onOpenOrders={() => setModal(user?.role === "customer" ? "orders" : { type: "admin", tab: "orders" })}
          onOpenBalance={() => setModal("balance")}
          onOpenCustomers={openCustomersSettings}
          onOpenDm={(dmId) => {
            setModal(null);
            setTab("chats");
            if (dmId) setActive({ type: "dm", id: dmId });
            if (isMobile) setMobSb(false);
            loadData();
          }}
          onChanged={refreshUnread}
        />
      )}
      {/* Telegram-style message context menu (react / reply / copy / pin) */}
      {msgCtx && (
        <div onClick={()=>setMsgCtx(null)}
             onContextMenu={(e)=>{ e.preventDefault(); setMsgCtx(null); }}
             style={{ position:"fixed",inset:0,zIndex:1000 }}>
          <div onClick={(e)=>e.stopPropagation()}
               style={{ position:"fixed",
                        top: Math.max(8, Math.min(msgCtx.y, (typeof window!=="undefined"?window.innerHeight:800)-260)),
                        left: Math.max(8, Math.min(msgCtx.x, (typeof window!=="undefined"?window.innerWidth:400)-236)),
                        width:224,background:T_NEW.paper,border:`1px solid ${T_NEW.ink12}`,
                        borderRadius:T_NEW.r2,boxShadow:T_NEW.shadowCard,overflow:"hidden" }}>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 8px",
                          borderBottom:`1px solid ${T_NEW.ink12}` }}>
              {REACTIONS.map(em => (
                <button key={em} onClick={()=>reactToMsg(msgCtx.msg, em)}
                        style={{ background:"none",border:"none",cursor:"pointer",fontSize:21,
                                 lineHeight:1,padding:"2px 1px" }}>{em}</button>
              ))}
              {/* Extended emoji selection */}
              <button onClick={()=>{ const mm=msgCtx; setMsgCtx(null);
                       setEmojiPicker({ mode:"react", msg:mm.msg,
                         position:{ top: Math.max(8, Math.min(mm.y, (typeof window!=="undefined"?window.innerHeight:800)-320)),
                                    left: Math.max(8, Math.min(mm.x, (typeof window!=="undefined"?window.innerWidth:400)-308)) } }); }}
                      title={t("app.reaction.more")}
                      style={{ background:T_NEW.paperSoft,border:"none",cursor:"pointer",
                               borderRadius:"50%",width:26,height:26,display:"flex",
                               alignItems:"center",justifyContent:"center",
                               color:T_NEW.ink56,fontSize:14,flexShrink:0 }}>▾</button>
            </div>
            {[
              ["↩︎", t("app.message.menu.reply"), ()=>openReply(msgCtx.msg), false, true],
              ["⧉", t("app.message.menu.copy"), ()=>copyMsg(msgCtx.msg), false, !!msgCtx.msg.text],
              ["📌", msgCtx.msg.pinnedAt ? t("app.message.menu.unpin") : t("app.message.menu.pin"),
                ()=>togglePinMsg(msgCtx.msg), false, true],
            ].filter(([,,,,show])=>show).map(([icon,label,onClick],i)=>(
              <button key={i} onClick={onClick}
                      style={{ display:"flex",alignItems:"center",gap:12,width:"100%",
                               padding:"11px 14px",background:"none",border:"none",cursor:"pointer",
                               textAlign:"left",fontSize:T_NEW.bodySm,color:T_NEW.ink,
                               fontFamily:T_NEW.fontSans }}>
                <span style={{ fontSize:16,width:18,textAlign:"center",color:T_NEW.ink56 }}>{icon}</span>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
      {emojiPicker && (
        <EmojiPicker position={emojiPicker.position}
                     onPick={onEmojiPick}
                     onClose={()=>setEmojiPicker(null)} />
      )}
      {modal==="orders" && (
        <OrdersModal
          user={user}
          onClose={()=>setModal(null)}
          onOpenDm={(dmId) => {
            setModal(null);
            setTab("chats");
            setActive({ type: "dm", id: dmId });
            if (isMobile) setMobSb(false);
            loadData();
          }}
        />
      )}
      {modal==="cart" && <CartModal onClose={()=>setModal(null)} onDone={()=>setModal("orders")} />}
      {modal?.type==="newPost" && (
        <NewPostModal
          channelSlug={modal.channelSlug}
          channels={channels}
          onClose={()=>setModal(null)}
          onDone={() => { loadData(); if (active?.type==="channel") loadChannelPosts(active.slug); }}
        />
      )}
      {modal?.type==="addTopic" && <NewTopicModal groupId={modal.groupId} onClose={()=>setModal(null)} onDone={loadData} />}
      {(modal==="admin" || modal?.type==="admin") && (
        <Modal title={t("app.adminPanel.title")} onClose={()=>setModal(null)} width={520}>
          <AdminPanel user={user} users={users} groups={groups} onRefresh={loadData}
            initialTab={modal?.type==="admin" ? modal.tab : undefined}
            onCreateGroup={()=>setModal("newGroup")}
            onAddTopic={(gid)=>setModal({type:"addTopic",groupId:gid})}
            onOpenDm={(dmId) => {
              // Jump straight into the order's customer DM: close the admin
              // panel, switch to chats, open the thread, refresh the DM list.
              setModal(null);
              setTab("chats");
              setActive({ type: "dm", id: dmId });
              if (isMobile) setMobSb(false);
              loadData();
            }} />
        </Modal>
      )}
      {modal==="quickAdd" && (
        <Modal title={t("app.quickAdd.title")} onClose={()=>setModal(null)} width={520}>
          <WarehouseQuickAddPage
            onClose={()=>setModal(null)}
            onPublished={() => { loadData(); }} />
        </Modal>
      )}

      {/* One-time onboarding hint for new customers. Internally checks
          localStorage so refreshes / multi-tab don't re-trigger it. */}
      <FirstRunHint user={user} onOpenForwardSettings={openForwardSettings} />

      {/* Application gate modal — opened from the banner above the
          channel feed. On close we re-fetch /me so any status flip
          (pending → submitted) propagates to the banner without a
          manual refresh. */}
      {appGateOpen && (
        <ApplicationModal
          user={user}
          onClose={async () => {
            setAppGateOpen(false);
            try {
              const r = await api.me();
              setUser(r.user);
            } catch { /* ignore */ }
          }}
          onApproved={async () => {
            try {
              const r = await api.me();
              setUser(r.user);
            } catch { /* ignore */ }
          }}
        />
      )}
    </div>
  );
}

// ChannelsList — sidebar channels list with brand-first grouping
// (migration 0055).
//
// Channels split into two groups:
//   * Brands — channels with brand_key set (Tommy Hilfiger, DKNY, etc.).
//     Collapsible. Empty brand channels hidden by default since the
//     whitelist seeds 65 channels and most start at 0 posts. Staff
//     and the "show empty" toggle override this.
//   * Categories — channels without brand_key (Walmart-*, raw channels
//     for staff). Always visible.
//
// Search box at the top of the sidebar (the existing one) filters by
// channel name across both groups. When the search is non-empty, both
// groups expand automatically so matches are findable.
//
// Group collapsed state persists in localStorage so a reseller's
// preference survives reloads.
function ChannelsList({ channels, search, active, isStaff, onPick, T, t, plural }) {
  const SB_KEY_BRANDS = "cg_sidebar_brands_collapsed";
  const SB_KEY_CATS   = "cg_sidebar_categories_collapsed";
  const SB_KEY_EMPTY  = "cg_sidebar_show_empty_brands";
  // Per-source collapse state lives under one key per retailer slug, e.g.
  // cg_sidebar_src_costco_collapsed. DEFAULT EXPANDED so resellers see the
  // Costco channels — a source is collapsed only when its key reads "1".
  const SB_KEY_SRC = (src) => `cg_sidebar_src_${src}_collapsed`;
  const [brandsCollapsed, setBrandsCollapsed] = useState(() => {
    try { return localStorage.getItem(SB_KEY_BRANDS) === "1"; } catch { return false; }
  });
  const [catsCollapsed, setCatsCollapsed] = useState(() => {
    try { return localStorage.getItem(SB_KEY_CATS) === "1"; } catch { return false; }
  });
  const [showEmptyBrands, setShowEmptyBrands] = useState(() => {
    try { return localStorage.getItem(SB_KEY_EMPTY) === "1"; } catch { return false; }
  });
  // The number of source groups is dynamic, so we can't call useState per
  // group (rules-of-hooks). Instead a single object-state map holds the
  // collapsed flag per source slug. We seed it lazily on first toggle by
  // reading localStorage in the toggle handler; lookups fall back to the
  // stored "1"/"0" value (default expanded). One hook, no loop.
  const [srcCollapsed, setSrcCollapsed] = useState({});
  const isSrcCollapsed = (src) => {
    if (src in srcCollapsed) return srcCollapsed[src];
    try { return localStorage.getItem(SB_KEY_SRC(src)) === "1"; } catch { return false; }
  };
  const toggleSrc = (src) => {
    setSrcCollapsed((m) => {
      const next = !isSrcCollapsed(src);
      try { localStorage.setItem(SB_KEY_SRC(src), next ? "1" : "0"); } catch {}
      return { ...m, [src]: next };
    });
  };
  const toggleBrands = () => {
    setBrandsCollapsed((v) => { try { localStorage.setItem(SB_KEY_BRANDS, v ? "0" : "1"); } catch {} return !v; });
  };
  const toggleCats = () => {
    setCatsCollapsed((v) => { try { localStorage.setItem(SB_KEY_CATS, v ? "0" : "1"); } catch {} return !v; });
  };
  const toggleEmpty = () => {
    setShowEmptyBrands((v) => { try { localStorage.setItem(SB_KEY_EMPTY, v ? "0" : "1"); } catch {} return !v; });
  };

  // Apply search across all channels. When search is non-empty we
  // bypass the collapse state so matches are always visible.
  const searchLower = (search || "").toLowerCase();
  const matchesSearch = (ch) => (ch.name || "").toLowerCase().includes(searchLower);
  const filtered = searchLower ? channels.filter(matchesSearch) : channels;

  // Store-first grouping (migrations 0062 + 0072): any channel carrying a
  // sourceRetailer nests under its STORE group (Costco / Walmart / Nordstrom
  // Rack) regardless of brandKey — resellers browse by store. The remainder
  // falls back to Brands (brandKey, no store) or flat Categories.
  const brands         = filtered.filter((ch) => ch.brandKey && !ch.sourceRetailer);
  const flatCategories = filtered.filter((ch) => !ch.brandKey && !ch.sourceRetailer);

  const SRC_DISPLAY = { costco: "Costco", walmart: "Walmart", "nordstrom-rack": "Nordstrom Rack" };
  const srcLabel = (src) =>
    SRC_DISPLAY[src] ||
    src.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  // Same comparator the brands list uses: publishedCount DESC, then name.
  const byCountThenName = (a, b) => {
    const ac = a.publishedCount || 0, bc = b.publishedCount || 0;
    if (ac !== bc) return bc - ac;
    return (a.name || "").localeCompare(b.name || "");
  };
  // Channel-visibility rule: hide EMPTY channels (0 posts) unless the
  // show-empty toggle is on or the user is searching. Applies to everyone
  // incl. staff — staff reveal empties via the per-group "show empty" toggle.
  // `items` is the total posts across the group (the count shown in the
  // header — products, not channel count).
  const isVisibleCh = (ch) =>
    showEmptyBrands || searchLower || (ch.publishedCount || 0) > 0;
  const sumItems = (list) => list.reduce((s, ch) => s + (ch.publishedCount || 0), 0);
  // How many channels in a list are empty (0 posts). Drives the show/hide-empty
  // toggle: it renders whenever empties EXIST (so it stays visible to re-hide
  // after revealing), not just when some are currently hidden.
  const emptyCount = (list) => list.filter((ch) => (ch.publishedCount || 0) === 0).length;

  // Group ALL sourced channels by retailer slug; drop groups with no visible
  // (non-empty) channels so empty store/raw feeds don't clutter the sidebar.
  const sourcedGroups = Object.entries(
    filtered
      .filter((ch) => ch.sourceRetailer)
      .reduce((acc, ch) => {
        (acc[ch.sourceRetailer] ||= []).push(ch);
        return acc;
      }, {}),
  )
    .map(([source, list]) => {
      const sorted = [...list].sort(byCountThenName);
      const visible = sorted.filter(isVisibleCh);
      return { source, label: srcLabel(source), channels: sorted, visible, items: sumItems(sorted) };
    })
    .filter((g) => g.visible.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label));

  const brandsVisible = brands.filter(isVisibleCh);
  // Brands sorted by publishedCount DESC so populated brands surface
  // first; ties broken by name. Empty brands (when shown) bubble to
  // the bottom alphabetically.
  brandsVisible.sort((a, b) => {
    const ac = a.publishedCount || 0, bc = b.publishedCount || 0;
    if (ac !== bc) return bc - ac;
    return (a.name || "").localeCompare(b.name || "");
  });

  // Force-expand both groups when searching so results are visible.
  const showBrands = !brandsCollapsed || searchLower;
  const showCats   = !catsCollapsed || searchLower;

  return (
    <>
      {/* Store groups (Costco / Walmart / Nordstrom Rack) — top-level so
          resellers browse by store; each is independently collapsible. */}
      {sourcedGroups.map((g) => {
        const showSrc = !isSrcCollapsed(g.source) || searchLower;
        return (
          <div key={g.source}>
            <GroupHeader
              label={g.label}
              count={g.items}
              collapsed={isSrcCollapsed(g.source) && !searchLower}
              onToggle={() => toggleSrc(g.source)}
              T={T}
            />
            {showSrc && (
              <div style={{ paddingLeft: 10, marginLeft: 14,
                            borderLeft: `1px solid ${T.border}` }}>
                {g.visible.map((ch) => (
                  <ChannelRow key={ch.id} ch={ch}
                              active={active}
                              onPick={onPick}
                              T={T} t={t} plural={plural} />
                ))}
                {!searchLower && emptyCount(g.channels) > 0 && (
                  <button type="button" onClick={toggleEmpty}
                          style={{
                            display: "block", width: "100%", textAlign: "left",
                            background: "transparent", border: "none",
                            color: T.textSec, fontSize: 12,
                            padding: "8px 16px", cursor: "pointer",
                            fontFamily: T_NEW.fontSans,
                          }}>
                    {showEmptyBrands ? t("sidebar.brand.hideEmpty") : t("sidebar.brand.showEmpty")}
                    {!showEmptyBrands && ` · ${emptyCount(g.channels)}`}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {brands.length > 0 && (
        <GroupHeader
          label={t("sidebar.group.brands")}
          count={sumItems(brands)}
          collapsed={brandsCollapsed && !searchLower}
          onToggle={toggleBrands}
          T={T}
        />
      )}
      {showBrands && brandsVisible.map((ch) => (
        <ChannelRow key={ch.id} ch={ch}
                    active={active}
                    onPick={onPick}
                    T={T} t={t} plural={plural} />
      ))}
      {/* Show-empty toggle, shown only when there are hidden empties
          and we're not in search mode. Helps a reseller discover the
          full brand list without forcing them to wade through it. */}
      {brands.length > 0 && showBrands && !searchLower && emptyCount(brands) > 0 && (
        <button type="button" onClick={toggleEmpty}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  background: "transparent", border: "none",
                  color: T.textSec, fontSize: 12,
                  padding: "8px 16px", cursor: "pointer",
                  fontFamily: T_NEW.fontSans,
                }}>
          {showEmptyBrands ? t("sidebar.brand.hideEmpty") : t("sidebar.brand.showEmpty")}
          {!showEmptyBrands && ` · ${emptyCount(brands)}`}
        </button>
      )}

      {flatCategories.length > 0 && (
        <GroupHeader
          label={t("sidebar.group.categories")}
          count={sumItems(flatCategories)}
          collapsed={catsCollapsed && !searchLower}
          onToggle={toggleCats}
          T={T}
        />
      )}
      {showCats && flatCategories.filter(isVisibleCh).map((ch) => (
        <ChannelRow key={ch.id} ch={ch}
                    active={active}
                    onPick={onPick}
                    T={T} t={t} plural={plural} />
      ))}
      {flatCategories.length > 0 && showCats && !searchLower && emptyCount(flatCategories) > 0 && (
        <button type="button" onClick={toggleEmpty}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  background: "transparent", border: "none",
                  color: T.textSec, fontSize: 12,
                  padding: "8px 16px", cursor: "pointer",
                  fontFamily: T_NEW.fontSans,
                }}>
          {showEmptyBrands ? t("sidebar.brand.hideEmpty") : t("sidebar.brand.showEmpty")}
          {!showEmptyBrands && ` · ${emptyCount(flatCategories)}`}
        </button>
      )}
    </>
  );
}

// Collapsible section heading shown above each group in the sidebar.
function GroupHeader({ label, count, collapsed, onToggle, T }) {
  return (
    <button type="button" onClick={onToggle}
            style={{
              display: "flex", width: "100%", alignItems: "center",
              justifyContent: "space-between", gap: 6,
              background: "transparent", border: "none",
              padding: "12px 14px 6px",
              cursor: "pointer",
              fontFamily: T_NEW.fontMono,
              fontSize: T_NEW.micro,
              letterSpacing: T_NEW.monoTrack,
              textTransform: "uppercase",
              color: T.textSec,
              fontWeight: 700,
            }}>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ display: "inline-block",
                       transform: collapsed ? "rotate(-90deg)" : "rotate(0)",
                       transition: "transform 140ms",
                       fontSize: 9 }}>▾</span>
        {label}
      </span>
      <span style={{ fontWeight: 600, color: T.textSec, opacity: 0.7 }}>
        {count}
      </span>
    </button>
  );
}

// Single channel row. Extracted so both brand and category lists
// render identical rows (only the grouping differs).
function ChannelRow({ ch, active, onPick, T, t, plural }) {
  const lang = useLang();
  const name = chName(ch, lang);
  const note = chNote(ch, lang);
  const isA = active?.type === "channel" && active?.slug === ch.slug;
  const isEmpty = (ch.publishedCount || 0) === 0;
  return (
    <div onClick={() => onPick(ch)}
         style={{
           display: "flex", alignItems: "center", gap: 12,
           padding: "10px 14px", cursor: "pointer",
           background: isA ? T.sidebarActive : "transparent",
           transition: "background 0.12s",
           opacity: isEmpty ? 0.55 : 1,
         }}
         onMouseEnter={(e) => { if (!isA) e.currentTarget.style.background = T.sidebarHover; }}
         onMouseLeave={(e) => { if (!isA) e.currentTarget.style.background = "transparent"; }}>
      <Av name={name} size={46} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 600, fontSize: 15,
          display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
        }}>
          <span>{name}</span>
          {ch.cashbackPct > 0 && (
            <span style={{
              background: `${T.online}25`, color: T.online,
              fontSize: 10, fontWeight: 700, padding: "1px 6px",
              borderRadius: 8, letterSpacing: 0.3,
            }}>
              {t("app.channels.cashback", { n: ch.cashbackPct })}
            </span>
          )}
        </div>
        <div style={{
          fontSize: 12, color: T.textSec,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {isEmpty
            ? t("sidebar.brand.empty")
            : t(plural(ch.publishedCount, "app.channels.posts"), { n: ch.publishedCount })}
          {note ? ` · ${note}` : ""}
        </div>
      </div>
    </div>
  );
}
